/**
 * Global Contact Modal
 * Handles opening/closing the global contact modal from any trigger
 *
 * Trigger attributes:
 * - data-contact-modal-trigger: Opens the modal
 * - data-contact-subject: Sets the inquiry subject fallback
 * - data-contact-product-title: Sets product title for inquiry
 * - data-contact-product-sku: Sets product SKU for inquiry
 * - data-contact-inquiry: Sets the inquiry field text
 * - data-contact-show-back: Show/hide back button (true/false)
 *
 * Events dispatched:
 * - contact-modal:open - When modal opens
 * - contact-modal:close - When modal closes
 *
 * Example usage:
 * <button data-contact-modal-trigger>Contact Us</button>
 * <button data-contact-modal-trigger data-contact-subject="Product Inquiry" data-contact-inquiry="Regarding: Diamond Ring">Inquire</button>
 */

class GlobalContactModal {
  constructor() {
    this.modal = document.querySelector('[data-contact-modal]');
    this.overlay = document.querySelector('[data-contact-modal-overlay]');
    this.closeBtn = document.querySelector('[data-contact-modal-close]');
    this.backBtn = document.querySelector('[data-contact-modal-back]');
    this.inquiryField = document.querySelector('[data-contact-inquiry-field]');

    console.log('[ContactModal] Initializing...', { modal: this.modal, overlay: this.overlay });

    if (!this.modal) {
      console.warn('[ContactModal] Modal element not found! Make sure contact-modal-global snippet is included.');
      return;
    }

    this.init();
  }

  init() {
    this.bindTriggers();
    this.bindCloseEvents();
    this.bindEscapeKey();
    this.bindInquirySuccessEvents();

    // Listen for custom open events
    document.addEventListener('contact-modal:request-open', (e) => {
      this.open(e.detail || {});
    });

    console.log('[ContactModal] Initialization complete. Ready to receive triggers.');
  }

  bindInquirySuccessEvents() {
    // The inline success "Close" button bubbles a close-request event.
    this.modal?.addEventListener('inquiry-form:close-request', () => this.close());
  }

  resetInquirySuccess() {
    // Clear any lingering success state from a previous submission so the form
    // is pristine when the modal is reopened.
    const region = this.modal?.querySelector('[data-inquiry-success-region]');
    if (region) region.innerHTML = '';
    const form = this.modal?.querySelector('[data-inquiry-form-container] form');
    if (form) {
      form.hidden = false;
      // Re-enable submit in case a prior submission left it busy
      const submit = form.querySelector('[type="submit"]');
      if (submit) {
        submit.removeAttribute('aria-busy');
        submit.disabled = false;
        if (submit.dataset.originalLabel) {
          submit.textContent = submit.dataset.originalLabel;
        }
      }
    }
    const errors = this.modal?.querySelectorAll('[data-inquiry-form-errors]');
    errors?.forEach((el) => el.remove());
  }

  bindTriggers() {
    // Use event delegation for dynamic triggers
    document.addEventListener('click', (e) => {
      console.log('[ContactModal] Click detected', { target: e.target });
      const trigger = e.target.closest('[data-contact-modal-trigger]');
      console.log('[ContactModal] Click detected', { target: e.target, trigger: trigger });
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        console.log('[ContactModal] Trigger found, opening modal...');
        this.handleTriggerClick(trigger);
      }
    });
  }

  handleTriggerClick(trigger) {
    const options = {
      subject: trigger.dataset.contactSubject || '',
      productTitle: trigger.dataset.contactProductTitle || '',
      productSku: trigger.dataset.contactProductSku || '',
      inquiry: trigger.dataset.contactInquiry || '',
      showBack: trigger.dataset.contactShowBack === 'true'
    };

    this.open(options);
  }

  bindCloseEvents() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Back button
    if (this.backBtn) {
      this.backBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
  }

  bindEscapeKey() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  open(options = {}) {
    console.log('[ContactModal] Opening modal with options:', options);
    const {
      subject = '',
      productTitle = '',
      productSku = '',
      inquiry = '',
      showBack = false,
      preserveSuccess = false
    } = options;

    // preserveSuccess: invoked after a successful submission redirected us back
    // and form-success.js mounted the success state. Skip everything that would
    // disturb that state — we just want to show the modal as-is.
    if (preserveSuccess) {
      this.modal.classList.add('is-open');
      this.overlay?.classList.add('is-visible');
      document.body.classList.add('contact-modal-open');
      const successHeading = this.modal.querySelector('[data-inquiry-success]');
      window.setTimeout(() => successHeading?.focus(), 400);
      document.dispatchEvent(new CustomEvent('contact-modal:open', { detail: options }));
      return;
    }

    // Set inquiry field
    if (this.inquiryField) {
      let inquiryValue = inquiry;
      if (!inquiryValue && productTitle) {
        inquiryValue = productSku
          ? `${productTitle} #${productSku}`
          : productTitle;
      } else if (!inquiryValue && subject) {
        inquiryValue = subject;
      }
      if (inquiryValue) {
        this.inquiryField.value = inquiryValue;
        this.inquiryField.setAttribute('readonly', '');
      } else {
        this.inquiryField.value = '';
        this.inquiryField.removeAttribute('readonly');
      }
    }

    // Toggle back button visibility
    if (this.backBtn) {
      this.backBtn.style.display = showBack ? 'flex' : 'none';
    }

    // Reset any lingering success state from a prior submission
    this.resetInquirySuccess();

    // Show modal
    console.log('[ContactModal] Adding is-open class to modal');
    this.modal.classList.add('is-open');
    this.overlay?.classList.add('is-visible');
    document.body.classList.add('contact-modal-open');
    console.log('[ContactModal] Modal should now be visible', { modalClasses: this.modal.className });

    // Focus first visible input inside the (re-shown) form
    setTimeout(() => {
      const form = this.modal.querySelector('[data-inquiry-form-container] form:not([hidden])');
      const firstInput = form?.querySelector('input:not([type="hidden"])') ||
                         this.modal.querySelector('input:not([type="hidden"])');
      firstInput?.focus();
    }, 400);

    // Dispatch open event
    document.dispatchEvent(new CustomEvent('contact-modal:open', {
      detail: options
    }));
  }

  close() {
    this.modal?.classList.remove('is-open');
    this.overlay?.classList.remove('is-visible');
    document.body.classList.remove('contact-modal-open');

    // Dispatch close event
    document.dispatchEvent(new CustomEvent('contact-modal:close'));
  }

  isOpen() {
    return this.modal?.classList.contains('is-open') || false;
  }
}

// Initialize
function initGlobalContactModal() {
  if (!window.globalContactModal) {
    window.globalContactModal = new GlobalContactModal();
  }
}

// DOM Ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGlobalContactModal);
} else {
  initGlobalContactModal();
}

// Shopify editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initGlobalContactModal);
}
